import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { getImageUrl } from '../utils/cdnUtils'
import { useWallet } from '../contexts/WalletContext'
import { useUserInfo } from '../contexts/UserInfoContext'
import { useTenantThemeConfig } from '../hooks/useTenantThemeConfig'
import { withdrawalSettingsService } from '../services/withdrawalSettingsService'
import { walletTransactionService } from '../services/walletTransactionService'

const translateWithdrawalErrorMessage = (errorMessage: string, formatCurrency: (amount: number) => string): string => {
  const normalizedMessage = String(errorMessage || '').trim()
  if (!normalizedMessage) {
    return 'เกิดข้อผิดพลาดในการส่งคำขอถอนเงิน'
  }

  const minimumMatch = normalizedMessage.match(/^Minimum withdrawal amount is\s+(\d+(?:\.\d+)?)$/i)
  if (minimumMatch) {
    return `ยอดถอนขั้นต่ำคือ ${formatCurrency(Number(minimumMatch[1]))}`
  }

  const maximumMatch = normalizedMessage.match(/^Maximum withdrawal amount per transaction is\s+(\d+(?:\.\d+)?)$/i)
  if (maximumMatch) {
    return `ยอดถอนสูงสุดต่อครั้งคือ ${formatCurrency(Number(maximumMatch[1]))}`
  }

  const dailyLimitMatch = normalizedMessage.match(/^Daily withdrawal request limit exceeded\s*\((\d+)\s+per day\)$/i)
  if (dailyLimitMatch) {
    return `คุณทำรายการถอนครบจำนวน ${dailyLimitMatch[1]} ครั้งต่อวันแล้ว`
  }

  const dailyAmountLimitMatch = normalizedMessage.match(/^Daily withdrawal amount limit exceeded\s*\((\d+(?:\.\d+)?)\s+per day\)$/i)
  if (dailyAmountLimitMatch) {
    return `ยอดถอนรวมต่อวันเกิน ${formatCurrency(Number(dailyAmountLimitMatch[1]))} แล้ว`
  }

  if (/^HTTP(?:\s+error!?\s*status:)?\s*\d+/i.test(normalizedMessage) || /^Failed to fetch$/i.test(normalizedMessage)) {
    return 'ไม่สามารถเชื่อมต่อระบบถอนได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง'
  }

  const mappedMessages: Record<string, string> = {
    'Validation failed': 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบแล้วลองใหม่อีกครั้ง',
    'Tenant ID and User ID are required': 'ไม่พบข้อมูลผู้ใช้งาน กรุณาเข้าสู่ระบบใหม่อีกครั้ง',
    'Invalid amount': 'จำนวนเงินถอนไม่ถูกต้อง',
    'Wallet not found': 'ไม่พบบัญชีกระเป๋าเงิน กรุณาติดต่อแอดมิน',
    'Withdrawal blocked because bonus turnover or bonus conditions are not complete': 'ยังมีเงื่อนไขโบนัสหรือ turnover ที่ต้องทำให้ครบก่อนถอนเงิน',
    'Withdrawal blocked because cashback turnover is not complete': 'ยังมีเงื่อนไข cashback turnover ที่ต้องทำให้ครบก่อนถอนเงิน',
    'Withdrawal requires bonus forfeiture before it can continue': 'รายการถอนนี้ต้องสละสิทธิ์โบนัสก่อนจึงจะดำเนินการต่อได้',
    'Insufficient wallet balance': 'ยอดเงินในกระเป๋าไม่เพียงพอ',
    'Invalid withdrawal amount': 'จำนวนเงินถอนไม่ถูกต้อง',
    'Unable to load withdrawal settings': 'ไม่สามารถโหลดเงื่อนไขการถอนได้ กรุณาลองใหม่อีกครั้ง',
  }

  return mappedMessages[normalizedMessage] || normalizedMessage
}

const getWithdrawalApprovalStatusText = (
  amount: number,
  autoApproveEnabled: boolean,
  maxAutoApprovalAmount: number,
  formatCurrency: (amount: number) => string
): string => {
  const exceedsAutoApprovalLimit =
    maxAutoApprovalAmount >= 0 && amount > maxAutoApprovalAmount

  if (autoApproveEnabled === false) {
    return 'ระบบปิดการอนุมัติอัตโนมัติ และรายการนี้จะรอเจ้าหน้าที่อนุมัติ'
  }

  if (exceedsAutoApprovalLimit) {
    return `ยอดนี้เกิน ${formatCurrency(maxAutoApprovalAmount)} และจะรอเจ้าหน้าที่อนุมัติ`
  }

  return 'อยู่ในเกณฑ์ดำเนินการอัตโนมัติ'
}

const WithdrawalPage: React.FC = () => {
  const navigate = useNavigate()
  const { walletData, formatCurrency, refreshWallet } = useWallet()
  const { getPrimaryBankAccount } = useUserInfo()
  const { themeConfig } = useTenantThemeConfig()
  const [withdrawalAmount, setWithdrawalAmount] = useState('')
  const [minWithdrawal, setMinWithdrawal] = useState(100) // Default value
  const [isLoadingSettings, setIsLoadingSettings] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const withdrawLockRef = useRef(false)
  const [withdrawalSettings, setWithdrawalSettings] = useState({
    minWithdrawalAmount: 100,
    gamesBeforeWithdrawal: 0,
    dailyWithdrawalLimit: 0,
    dailyWithdrawalAmountLimit: 0,
    autoWithdrawalFromFirstDeposit: 'disabled',
    autoApproveEnabled: true,
    maxAutoApprovalAmount: 4999,
    maxWithdrawalPerTransaction: 30000,
    shutdownTime: '23:40',
    activationTime: '08:00'
  })
  const availableBalance = walletData?.balance?.main || 0
  const primaryBankAccount = getPrimaryBankAccount()
  const systemWithdrawEnabled = themeConfig?.settings?.withdrawEnabled !== false

  // Load withdrawal settings on component mount
  useEffect(() => {
    const fetchWithdrawalSettings = async () => {
      try {
        setIsLoadingSettings(true)
        const response = await withdrawalSettingsService.getWithdrawalSettings()
        
        if (response.success && response.data) {
          const settings = response.data.settings
          
          // Update all withdrawal settings
          setWithdrawalSettings({
            minWithdrawalAmount: settings.withdrawalConditions.minWithdrawalAmount,
            gamesBeforeWithdrawal: settings.withdrawalConditions.gamesBeforeWithdrawal,
            dailyWithdrawalLimit: settings.withdrawalConditions.dailyWithdrawalLimit,
            dailyWithdrawalAmountLimit: settings.withdrawalConditions.dailyWithdrawalAmountLimit || 0,
            autoWithdrawalFromFirstDeposit: settings.withdrawalConditions.autoWithdrawalFromFirstDeposit,
            autoApproveEnabled: settings.autoWithdrawalConditions.autoApproveEnabled !== false,
            maxAutoApprovalAmount: settings.autoWithdrawalConditions.maxAutoApprovalAmount,
            maxWithdrawalPerTransaction: settings.autoWithdrawalConditions.maxWithdrawalPerTransaction,
            shutdownTime: settings.withdrawalBot.shutdownTime,
            activationTime: settings.withdrawalBot.activationTime
          })
          
          setMinWithdrawal(settings.withdrawalConditions.minWithdrawalAmount)
          
          console.log('✅ Loaded withdrawal settings:', {
            settings: settings.withdrawalConditions,
            autoWithdrawal: settings.autoWithdrawalConditions,
            botSettings: settings.withdrawalBot,
            fromCache: (response.data as any).fromCache
          })
        } else {
          console.error('Failed to fetch withdrawal settings, using default:', (response as any).error)
          // Show warning toast for failed API call but continue with defaults
          Swal.fire({
            icon: 'warning',
            title: 'โหลดข้อมูลไม่สำเร็จ',
            text: 'ใช้การตั้งค่าเริ่มต้น กรุณาลองใหม่อีกครั้ง',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true
          })
        }
      } catch (error) {
        console.error('Error fetching withdrawal settings:', error)
        // Show error toast for network errors
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true
        })
      } finally {
        setIsLoadingSettings(false)
      }
    }

    fetchWithdrawalSettings()
  }, [])

  // Check withdrawal conditions
  const checkWithdrawalConditions = (_amount: number) => {
    const currentTime = new Date()
    const currentHour = currentTime.getHours()
    const currentMinute = currentTime.getMinutes()
    // const currentTimeStr = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`
    
    // Parse bot shutdown/activation times
    const [shutdownHour, shutdownMinute] = withdrawalSettings.shutdownTime.split(':').map(Number)
    const [activationHour, activationMinute] = withdrawalSettings.activationTime.split(':').map(Number)
    
    const shutdownTimeMinutes = shutdownHour * 60 + shutdownMinute
    const activationTimeMinutes = activationHour * 60 + activationMinute
    const currentTimeMinutes = currentHour * 60 + currentMinute
    
    // Check if withdrawal bot is active (between activation and shutdown time)
    let isBotActive = true
    if (shutdownTimeMinutes > activationTimeMinutes) {
      // Normal case: activation < shutdown (e.g., 08:00 - 23:45)
      isBotActive = currentTimeMinutes >= activationTimeMinutes && currentTimeMinutes < shutdownTimeMinutes
    } else {
      // Cross midnight case: shutdown < activation (e.g., 23:45 - 08:00)
      isBotActive = currentTimeMinutes >= activationTimeMinutes || currentTimeMinutes < shutdownTimeMinutes
    }
    
    if (!isBotActive) {
      return {
        isValid: false,
        message: `ระบบถอนเงินปิดชั่วคราว เปิดให้บริการเวลา ${withdrawalSettings.shutdownTime} - ${withdrawalSettings.activationTime}`
      }
    }
    
    // Check daily withdrawal limit (if enabled)
    if (withdrawalSettings.dailyWithdrawalLimit > 0) {
      // TODO: Check actual daily withdrawal count from user data
      // For now, we'll skip this check
    }
    
    return { isValid: true }
  }

  const handleWithdrawal = async () => {
    if (withdrawLockRef.current || isSubmitting) {
      return
    }

    withdrawLockRef.current = true
    setIsSubmitting(true)

    try {
    if (!systemWithdrawEnabled) {
      await Swal.fire({
        icon: 'error',
        title: 'ระบบถอนเงินปิดชั่วคราว',
        text: 'ขณะนี้ผู้ดูแลระบบปิดการถอนเงินทั้งระบบ กรุณาลองใหม่ภายหลัง',
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#dc3545'
      })
      return
    }

    const amount = parseFloat(withdrawalAmount)
    
    if (!withdrawalAmount || isNaN(amount)) {
      await Swal.fire({
        icon: 'warning',
        title: 'ข้อมูลไม่ครบถ้วน',
        text: 'กรุณาใส่จำนวนเงินที่ต้องการถอน',
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#ffc107'
      })
      return
    }
    
    if (amount < minWithdrawal) {
      await Swal.fire({
        icon: 'warning',
        title: 'จำนวนเงินไม่ถูกต้อง',
        text: `ถอนเงินขั้นต่ำ ${minWithdrawal} บาท`,
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#ffc107'
      })
      return
    }

    if (
      withdrawalSettings.maxWithdrawalPerTransaction > 0 &&
      amount > withdrawalSettings.maxWithdrawalPerTransaction
    ) {
      await Swal.fire({
        icon: 'warning',
        title: 'ยอดถอนเกินกำหนด',
        text: `ถอนเงินได้ไม่เกิน ${formatCurrency(withdrawalSettings.maxWithdrawalPerTransaction)} ต่อครั้ง`,
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#ffc107'
      })
      return
    }
    
    if (amount > availableBalance) {
      await Swal.fire({
        icon: 'error',
        title: 'ยอดเงินไม่เพียงพอ',
        text: `ยอดเงินคงเหลือ ${formatCurrency(availableBalance)}`,
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#dc3545'
      })
      return
    }

    const requiresManualApproval =
      withdrawalSettings.autoApproveEnabled === false ||
      (
        withdrawalSettings.maxAutoApprovalAmount >= 0 &&
        amount > withdrawalSettings.maxAutoApprovalAmount
      )
    
    // Check withdrawal conditions
    const conditionCheck = checkWithdrawalConditions(amount)
    if (!conditionCheck.isValid) {
      await Swal.fire({
        icon: 'error',
        title: 'ไม่สามารถถอนเงินได้',
        text: conditionCheck.message,
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#dc3545'
      })
      return
    }
    
    // Check if loading settings
    if (isLoadingSettings) {
      await Swal.fire({
        icon: 'info',
        title: 'กำลังโหลดข้อมูล',
        text: 'กำลังโหลดข้อมูลการตั้งค่า กรุณารอสักครู่',
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#17a2b8'
      })
      return
    }

    if (!primaryBankAccount) {
      await Swal.fire({
        icon: 'warning',
        title: 'ยังไม่พบบัญชีธนาคาร',
        text: 'กรุณาเพิ่มบัญชีธนาคารก่อนทำรายการถอนเงิน',
        confirmButtonText: 'ไปหน้าบัญชี',
        confirmButtonColor: '#17a2b8'
      })
      navigate('/profile?openAddBank=1')
      return
    }
    
    // Show confirmation dialog
    const result = await Swal.fire({
      icon: 'question',
      title: 'ยืนยันการถอนเงิน',
      html: `
        <div style="text-align: left;">
          <p><strong>จำนวนเงิน:</strong> ${formatCurrency(amount)}</p>
          <p><strong>บัญชีปลายทาง:</strong> ${primaryBankAccount.bankName}</p>
          <p><strong>หมายเลขบัญชี:</strong> ${primaryBankAccount.accountNumber}</p>
          <p><strong>ชื่อบัญชี:</strong> ${primaryBankAccount.accountName}</p>
          <p><strong>สถานะ:</strong> ${getWithdrawalApprovalStatusText(
            amount,
            withdrawalSettings.autoApproveEnabled,
            withdrawalSettings.maxAutoApprovalAmount,
            formatCurrency
          )}</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#dc3545',
      reverseButtons: true
    })
    
    if (result.isConfirmed) {
      Swal.fire({
        title: 'กำลังดำเนินการ...',
        text: 'กรุณารอสักครู่',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading()
        }
      })

      try {
        const response = await walletTransactionService.createWithdrawal({
          amount,
          bankAccount: {
            bankCode: primaryBankAccount.bankCode,
            accountNumber: primaryBankAccount.accountNumber,
            accountName: primaryBankAccount.accountName
          },
          reason: 'user_withdrawal_request'
        })

        if (!response.success) {
          if (response.code === 'WITHDRAW_REQUEST_COOLDOWN_ACTIVE') {
            throw new Error(
              response.data?.userMessage
              || response.message
              || 'คุณเพิ่งส่งคำขอถอนเงินไปแล้ว กรุณารอสักครู่แล้วลองใหม่อีกครั้ง'
            )
          }
          throw new Error(response.message || response.error || 'ไม่สามารถส่งคำขอถอนเงินได้')
        }

        await refreshWallet()
        setWithdrawalAmount('')

        await Swal.fire({
          icon: 'success',
          title: 'ส่งคำขอถอนเงินสำเร็จ',
          text: requiresManualApproval
            ? `คำขอถอนเงิน ${formatCurrency(amount)} ถูกส่งแล้ว และกำลังรอเจ้าหน้าที่อนุมัติ`
            : `คำขอถอนเงิน ${formatCurrency(amount)} ถูกส่งเรียบร้อยแล้ว`,
          confirmButtonText: 'ดูรายการถอน',
          confirmButtonColor: '#28a745'
        })

        navigate('/transaction')
      } catch (error: any) {
        await Swal.fire({
          icon: 'error',
          title: 'ถอนเงินไม่สำเร็จ',
          text: translateWithdrawalErrorMessage(error?.message || '', formatCurrency),
          confirmButtonText: 'ตกลง',
          confirmButtonColor: '#dc3545'
        })
      }
    }
    } finally {
      withdrawLockRef.current = false
      setIsSubmitting(false)
    }
  }

  const handleCancel = async () => {
    const result = await Swal.fire({
      icon: 'question',
      title: 'ยกเลิกการถอนเงิน',
      text: 'คุณต้องการยกเลิกการถอนเงินหรือไม่?',
      showCancelButton: true,
      confirmButtonText: 'ยกเลิก',
      cancelButtonText: 'ไม่',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      reverseButtons: true
    })
    
    if (result.isConfirmed) {
      navigate('/dashboard')
    }
  }

  // Removed old useEffect - withdrawal conditions now handled by checkWithdrawalConditions

  if (!systemWithdrawEnabled) {
    return (
      <div className="content pb-3">
        <div className="container">
          <div className="row px--1">
            <div className="col-12 px-1">
              <div className="bdr-5 bx-sd mb-2">
                <div className="row mx--1">
                  <div className="col-12 px-1">
                    <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                      <a
                        href="#"
                        className="txt-link-d"
                        onClick={(e) => {
                          e.preventDefault()
                          navigate('/dashboard')
                        }}
                      >
                        <i className="fas fa-chevron-left me-1"></i>
                        หน้าหลัก
                      </a>
                    </div>
                  </div>
                  <div className="col-12 px-1">
                    <div className="w-100 p-4 bgc-7">
                      <div className="text-center">
                        <div className="mb-3" style={{ fontSize: '2rem', color: '#dc3545' }}>
                          <i className="fas fa-ban"></i>
                        </div>
                        <h4 className="mb-2">ระบบถอนเงินปิดชั่วคราว</h4>
                        <p className="mb-0 text-muted">
                          ขณะนี้ผู้ดูแลระบบปิดการถอนเงินทั้งระบบ กรุณาลองใหม่ภายหลัง
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row px--1">
          <div className="col-12 px-1">
            <div className="bdr-5 bx-sd mb-2">
              <div className="row mx--1">
                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                    <a 
                      href="#" 
                      className="txt-link-d"
                      onClick={(e) => {
                        e.preventDefault()
                        navigate('/dashboard')
                      }}
                    >
                      <i className="fas fa-chevron-left me-1"></i>
                      หน้าหลัก
                    </a>
                  </div>
                </div>

                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-7">
                    <div className="bdr-5 bgc-4 c-red px-2 py-1 fs-18 t-medium mb-2">
                      <i className="fas fa-wallet me-2"></i>
                      แจ้งถอนเงิน
                    </div>

                    <div className="bdr-5 bgc-4 c-red p-2">
                      <div className="c-dark t-medium mb-2">บัญชีธนาคารของท่าน</div>

                      <div className="row mx--1">
                        <div className="col-12 px-1 mb-2">
                          {primaryBankAccount ? (
                            <div className="wrap align-items-start bgc-7 bdr-5 p-2">
                              <div className="img-b bdr-5 bgc-4">
                                <img src={getImageUrl('icons-kbank.png')} alt="" />
                              </div>

                              <div className="txt-b ms-3">
                                <h6 className="c-dark">{primaryBankAccount.bankName}</h6>
                                <h5 className="c-red t-medium">{primaryBankAccount.accountNumber}</h5>
                                <h6
                                  className="c-white bgg-5 bdr-5 px-2 mb-1"
                                  style={{ width: 'fit-content' }}
                                >
                                  ชื่อบัญชี
                                </h6>
                                <h5 className="c-dark t-medium fs-16">
                                  {primaryBankAccount.accountName}
                                </h5>
                              </div>
                            </div>
                          ) : (
                            <div className="wrap align-items-center bgc-7 bdr-5 p-3 text-center">
                              <h6 className="c-dark mb-0">
                                ไม่พบข้อมูลบัญชีธนาคาร กรุณาติดต่อเจ้าหน้าที่
                              </h6>
                            </div>
                          )}
                        </div>

                        <div className="col-12 px-1 mb-2">
                          <div className="bgc-7 bdr-5 px-2 py-2">
                            <p className="c-dark mb-2">จำนวนเงินที่ถอนได้</p>

                            <div className="bgg-1 p-2 bdr-5 text-center">
                              <h4 className="c-white t-regular">{formatCurrency(availableBalance)}</h4>
                            </div>
                          </div>
                        </div>

                        <div className="col-12 px-1 mb-2">
                          <div className="bgc-7 bdr-5 px-2 py-2 mb-2">
                            <p className="c-dark mb-2">จำนวนเงินที่ต้องการถอน</p>
                            <input
                              type="number"
                              id="amount"
                              className="input-show border-0 bdr-5"
                              placeholder="ใส่จำนวนเงิน"
                              value={withdrawalAmount}
                              onChange={(e) => setWithdrawalAmount(e.target.value)}
                              min={minWithdrawal}
                              max={availableBalance}
                              disabled={isSubmitting}
                            />
                          </div>

                          <h6 className="c-dark">
                            {isLoadingSettings ? (
                              <>ถอนเงินขั้นต่ำ <span className="text-muted">กำลังโหลด...</span> บาท</>
                            ) : (
                              <>ถอนเงินขั้นต่ำ {minWithdrawal} บาท</>
                            )}
                          </h6>
                          
                          {/* Additional withdrawal conditions */}
                          {!isLoadingSettings && (
                            <div className="mt-2">
                              {/* {withdrawalSettings.dailyWithdrawalLimit > 0 && (
                                <small className="c-dark d-block">
                                  จำกัดการถอนต่อวัน: {withdrawalSettings.dailyWithdrawalLimit} ครั้ง
                                </small>
                              )} */}
                              {/* {withdrawalSettings.gamesBeforeWithdrawal > 0 && (
                                <small className="c-dark d-block">
                                  ต้องเล่นเกมส์ก่อนถอน: {withdrawalSettings.gamesBeforeWithdrawal} ครั้ง
                                </small>
                              )} */}
                              <small className="c-dark d-block">
                                ถอนได้ไม่เกินต่อครั้ง: {formatCurrency(withdrawalSettings.maxWithdrawalPerTransaction)}
                              </small>
                              <small className="c-dark d-block">
                              เวลาปิดบริการถอน: {withdrawalSettings.shutdownTime} - {withdrawalSettings.activationTime}
                              </small>
                            </div>
                          )}
                        </div>

                        <div className="col-6 px-1">
                          <button
                            type="button"
                            className="check-button w-100 bgg-5 border-0 c-white"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                          >
                            <i className="fas fa-trash-alt me-2"></i>
                            ยกเลิก
                          </button>
                        </div>

                        <div className="col-6 px-1">
                          <button
                            type="button"
                            className="check-button w-100 bgg-4 border-0 c-white"
                            onClick={handleWithdrawal}
                            disabled={isLoadingSettings || !primaryBankAccount || isSubmitting}
                            aria-busy={isSubmitting}
                            style={isSubmitting ? { opacity: 0.75, pointerEvents: 'none' } : undefined}
                          >
                            <i className={`fas ${isSubmitting ? 'fa-spinner fa-spin' : 'fa-hand-pointer'} me-2`}></i>
                            {isSubmitting ? 'กำลังดำเนินการ...' : 'ถอนเงิน'}
                          </button>
                        </div>
                      </div>

                      {/* Withdrawal status message - now handled by checkWithdrawalConditions */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WithdrawalPage
